import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import { createUserProfile, getUserProfile, updateUserProfile } from '../lib/firestore';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

// Admin email whitelist (set your admin emails here)
const ADMIN_EMAILS = ['admin@prestigemesbah.com', 'admin@prestige-mesbah.com', 'admin@hjrmesbah.com'];
const isAdminEmail = (email = '') => ADMIN_EMAILS.includes(email.toLowerCase());

const profileFromFirebaseUser = (fbUser) => ({
  id: fbUser.uid,
  name: fbUser.displayName || fbUser.email?.split('@')[0] || 'User',
  email: fbUser.email,
  phone: fbUser.phoneNumber || '',
  role: isAdminEmail(fbUser.email) ? 'admin' : 'customer',
});

const isPermissionDenied = (error) =>
  error?.code === 'permission-denied' ||
  error?.code === 'firestore/permission-denied' ||
  error?.message?.toLowerCase().includes('permission');

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      // Reset on every auth change, not just the initial mount — otherwise a route
      // guard that reads `loading` (e.g. AdminLayout) sees stale loading=false plus a
      // still-null profile right after login (profile fetch below hasn't resolved yet)
      // and bounces the freshly-logged-in admin straight back to /login.
      setLoading(true);
      if (fbUser) {
        setUser(fbUser);
        const fallbackProfile = profileFromFirebaseUser(fbUser);

        try {
          // Fetch or create user profile. If Firestore rules block this, keep
          // auth usable and rely on the local admin email whitelist.
          let p = await getUserProfile(fbUser.uid);
          if (!p) {
            const { id, ...newProfile } = fallbackProfile;
            try {
              await createUserProfile(fbUser.uid, newProfile);
            } catch (writeError) {
              if (!isPermissionDenied(writeError)) throw writeError;
              console.warn('Could not create Firestore user profile:', writeError);
            }
            p = fallbackProfile;
          } else if (isAdminEmail(fbUser.email) && p.role !== 'admin') {
            try {
              await updateUserProfile(fbUser.uid, { role: 'admin' });
            } catch (writeError) {
              if (!isPermissionDenied(writeError)) throw writeError;
              console.warn('Could not update Firestore admin role:', writeError);
            }
            p.role = 'admin';
          }
          setProfile(p);
        } catch (e) {
          if (!isPermissionDenied(e)) {
            console.error('Failed to load user profile:', e);
          } else {
            console.warn('Firestore profile read denied; using auth profile fallback:', e);
          }
          setProfile(fallbackProfile);
        }
      } else {
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const login = async (email, password) => {
    try {
      const res = await signInWithEmailAndPassword(auth, email, password);
      return {
        success: true,
        user: {
          id: res.user.uid,
          email: res.user.email,
          role: isAdminEmail(res.user.email) ? 'admin' : 'customer',
        },
      };
    } catch (e) {
      return { success: false, error: mapAuthError(e.code) };
    }
  };

  const register = async (name, email, password, phone) => {
    try {
      const res = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(res.user, { displayName: name });
      const newProfile = {
        name,
        email,
        phone: phone || '',
        role: isAdminEmail(email) ? 'admin' : 'customer',
      };
      try {
        await createUserProfile(res.user.uid, newProfile);
      } catch (e) {
        if (!isPermissionDenied(e)) throw e;
        console.warn('Could not create Firestore user profile:', e);
      }
      return { success: true, user: res.user };
    } catch (e) {
      return { success: false, error: mapAuthError(e.code) };
    }
  };

  const logout = async () => {
    await signOut(auth);
  };

  // Re-pulls the Firestore profile doc without a full auth round-trip — used after
  // writing to it directly (e.g. saving the shipping address at checkout) so the new
  // fields show up immediately instead of only after the next login/reload.
  const refreshProfile = async () => {
    if (!user) return;
    try {
      const p = await getUserProfile(user.uid);
      if (p) setProfile(p);
    } catch (e) {
      console.warn('Failed to refresh user profile:', e);
    }
  };

  const isAdmin = profile?.role === 'admin';
  const currentUser = profile ? { id: user?.uid, ...profile } : null;

  return (
    <AuthContext.Provider value={{ user: currentUser, firebaseUser: user, profile, login, register, logout, refreshProfile, isAdmin, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const mapAuthError = (code) => {
  const map = {
    'auth/invalid-credential': 'Invalid email or password',
    'auth/user-not-found': 'User not found',
    'auth/wrong-password': 'Wrong password',
    'auth/email-already-in-use': 'Email already registered',
    'auth/weak-password': 'Password too weak (min 6 chars)',
    'auth/invalid-email': 'Invalid email address',
    'auth/network-request-failed': 'Network error',
  };
  return map[code] || code;
};
